import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, ArrowRight, Send } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { t, language } = useLanguage();

  const contactSchema = z.object({
    name: z.string().min(2, language === 'es' ? 'El nombre debe tener al menos 2 caracteres' : 'Name must have at least 2 characters'),
    email: z.string().email(language === 'es' ? 'Correo electrónico inválido' : 'Invalid email address'),
    phone: z.string().min(10, language === 'es' ? 'El teléfono debe tener al menos 10 dígitos' : 'Phone must have at least 10 digits'),
    message: z.string().min(10, language === 'es' ? 'El mensaje debe tener al menos 10 caracteres' : 'Message must have at least 10 characters'),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...data,
        service: 'Consulta General',
      });
      toast.success(language === 'es' ? 'Mensaje enviado. Le contactaremos pronto.' : 'Message sent. We will contact you soon.');
      reset();
    } catch (error) {
      toast.error(language === 'es' ? 'Error al enviar. Por favor intente nuevamente.' : 'Error sending. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-32 bg-gradient-to-b from-[#F8F6F1] to-[#EFECE5] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      {/* Decorative corner accents */}
      <div className="absolute top-8 left-8 w-24 h-24 border-l-2 border-t-2 border-[#C5A55C]/30" />
      <div className="absolute top-8 right-8 w-24 h-24 border-r-2 border-t-2 border-[#C5A55C]/30" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-2 gap-20">
          {/* Left Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <motion.span 
              className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              {t('contact.tagline')}
            </motion.span>
            
            <h2 className="font-montserrat font-light text-4xl md:text-5xl text-[#1A1A1A] tracking-[0.08em] uppercase mb-6 leading-tight">
              {t('contact.title1')}
              <br />
              <span className="text-[#C5A55C]">{t('contact.title2')}</span>
            </h2>
            
            <div className="w-16 h-[2px] bg-[#C5A55C] mb-8" />
            
            <p className="font-raleway font-normal text-base text-[#1A1A1A]/60 leading-relaxed tracking-wide mb-12 max-w-md">
              {t('contact.description')}
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" data-testid="contact-form">
              {/* Name */}
              <div className="relative">
                <input
                  {...register('name')}
                  type="text"
                  placeholder={t('contact.form.name')}
                  className="w-full bg-white border-2 border-[#1A1A1A]/10 py-4 px-6 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C5A55C] transition-colors duration-300 font-raleway tracking-wide"
                  data-testid="contact-name-input"
                />
                {errors.name && (
                  <span className="text-red-500 text-xs mt-2 block font-light">{errors.name.message}</span>
                )}
              </div>

              {/* Email */}
              <div className="relative">
                <input
                  {...register('email')}
                  type="email"
                  placeholder={t('contact.form.email')}
                  className="w-full bg-white border-2 border-[#1A1A1A]/10 py-4 px-6 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C5A55C] transition-colors duration-300 font-raleway tracking-wide"
                  data-testid="contact-email-input"
                />
                {errors.email && (
                  <span className="text-red-500 text-xs mt-2 block font-light">{errors.email.message}</span>
                )}
              </div>

              {/* Phone */}
              <div className="relative">
                <input
                  {...register('phone')}
                  type="tel"
                  placeholder={t('contact.form.phone')}
                  className="w-full bg-white border-2 border-[#1A1A1A]/10 py-4 px-6 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C5A55C] transition-colors duration-300 font-raleway tracking-wide"
                  data-testid="contact-phone-input"
                />
                {errors.phone && (
                  <span className="text-red-500 text-xs mt-2 block font-light">{errors.phone.message}</span>
                )}
              </div>

              {/* Message */}
              <div className="relative">
                <textarea
                  {...register('message')}
                  placeholder={t('contact.form.message')}
                  rows={4}
                  className="w-full bg-white border-2 border-[#1A1A1A]/10 py-4 px-6 text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C5A55C] transition-colors duration-300 font-raleway tracking-wide resize-none"
                  data-testid="contact-message-input"
                />
                {errors.message && (
                  <span className="text-red-500 text-xs mt-2 block font-light">{errors.message.message}</span>
                )}
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group mt-8 px-12 py-5 bg-[#C5A55C] text-white hover:bg-[#1A1A1A] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-4 shadow-lg hover:shadow-xl"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
            </form>
          </motion.div>

          {/* Right Column - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            className="lg:pl-16"
          >
            <div className="bg-[#1A1A1A] p-12 relative">
              {/* Gold corner accent */}
              <div className="absolute top-0 left-0 w-16 h-16 border-l-4 border-t-4 border-[#C5A55C]" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-r-4 border-b-4 border-[#C5A55C]" />
              
              <div className="space-y-10">
                <ContactInfoItem
                  icon={Phone}
                  title={t('contact.info.phone')}
                  content="(215) 555-0123"
                  link="tel:+12155550123"
                />
                <ContactInfoItem
                  icon={Mail}
                  title={t('contact.info.email')}
                  content="info@martinez-solutions.com"
                  link="mailto:info@martinez-solutions.com"
                />
                <ContactInfoItem
                  icon={MapPin}
                  title={t('contact.info.location')}
                  content="Philadelphia, PA"
                />
              </div>

              {/* Schedule Note */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <span className="font-montserrat font-medium text-[10px] tracking-[0.25em] text-[#C5A55C] uppercase block mb-4">
                  {t('contact.info.hours')}
                </span>
                <p className="font-raleway font-light text-sm text-white/60 tracking-wide">
                  {t('contact.info.weekdays')}: 8:00 AM - 6:00 PM
                  <br />
                  {t('contact.info.saturday')}: 9:00 AM - 4:00 PM
                </p>
              </div>

              {/* Logo */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ delay: 0.8, duration: 1 }}
                className="mt-12"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_734cb631-c213-4223-9ec5-3c4d6675302f/artifacts/573hpmih_Transparent-PNG.png"
                  alt="Martinez Solutions"
                  className="h-16 opacity-50"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Decorative corner accents bottom */}
      <div className="absolute bottom-8 left-8 w-24 h-24 border-l-2 border-b-2 border-[#C5A55C]/30" />
      <div className="absolute bottom-8 right-8 w-24 h-24 border-r-2 border-b-2 border-[#C5A55C]/30" />
    </section>
  );
};

const ContactInfoItem = ({ icon: Icon, title, content, link }) => (
  <div className="group">
    <div className="flex items-start gap-6">
      <div className="w-12 h-12 bg-[#C5A55C]/20 group-hover:bg-[#C5A55C] flex items-center justify-center transition-all duration-300">
        <Icon className="w-5 h-5 text-[#C5A55C] group-hover:text-white stroke-[1.5] transition-colors duration-300" />
      </div>
      <div>
        <span className="font-montserrat font-medium text-[10px] tracking-[0.25em] text-white/40 uppercase block mb-2">
          {title}
        </span>
        {link ? (
          <a
            href={link}
            className="font-raleway font-normal text-lg text-white hover:text-[#C5A55C] transition-colors duration-300 tracking-wide"
          >
            {content}
          </a>
        ) : (
          <span className="font-raleway font-normal text-lg text-white tracking-wide">
            {content}
          </span>
        )}
      </div>
    </div>
  </div>
);

export default ContactSection;
